/* eslint-disable @typescript-eslint/no-explicit-any */
import {Box, Button, Stack, TextField} from '@mui/material';
import {GlobalModal} from '../../common/GlobalModal/GlobalModal';
import {MainViewContainer} from '../../common/MainViewContainer/MainViewContainer';
import {useCallback, useEffect, useState} from 'react';
import {DataTable} from '../../common/DataTable/DataTable';
import {useEquipment} from '../../providers/api/useEquipment';
import {equipmentColumns} from './equipmentViewData';
import {GridCellParams} from '@mui/x-data-grid';
import {Toast} from '../../common/Alerts/Toast';
import {ToastStruct} from '../../types/types';
import {Confirm} from '../../common/Alerts/Confirm';

export const EquipmentView = () => {
  const {listEquipment, deleteEquipment, createEquipment, updateEquipment} =
    useEquipment();
  const [openAdd, setOpenAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [equipment, setEquipment] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState<ToastStruct>({
    severity: 'info',
    text: '',
    verticalPos: 'bottom',
    horizontalPos: 'center',
  });
  const [name, setName] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmAdd, setShowConfirmAdd] = useState(false);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [currentId, setCurrentId] = useState('');
  const [mode, setMode] = useState('');

  const getData = useCallback(async () => {
    const data: any = await listEquipment();
    setEquipment(data);
    setLoading(false);
  }, [listEquipment]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleDelete = async (id: string) => {
    const result: any = await deleteEquipment(id);
    if (result) {
      setToastData({
        ...toastData,
        severity: 'success',
        text: 'Equipment has been deleted',
      });
    } else {
      setToastData({
        ...toastData,
        severity: 'error',
        text: 'Error, could not delete the equipment',
      });
    }
    setShowToast(true);
    getData();
  };

  const handleUpdate = async (id: string) => {
    const result: any = await updateEquipment(id, name);
    if (result) {
      setToastData({
        ...toastData,
        severity: 'success',
        text: 'Equipment has been updated',
      });
    } else {
      setToastData({
        ...toastData,
        severity: 'error',
        text: 'Error, could not update the equipment',
      });
    }
    setShowToast(true);
    getData();
  };

  const handleAddEquipment = async () => {
    const result = await createEquipment({
      name: name,
      status: 'active',
    });
    if (result) {
      setToastData({
        ...toastData,
        severity: 'success',
        text: 'Equipment has been created',
      });
    } else {
      setToastData({
        ...toastData,
        severity: 'error',
        text: 'Error, could not create the equipment',
      });
    }
    setName('');
    setOpenAdd(false);
    setShowToast(true);
    getData();
  };

  return (
    <>
      <MainViewContainer title="Equipment">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '15px',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setMode('Add');
              setName('');
              setOpenAdd(true);
            }}
          >
            Add Equipment
          </Button>
        </Box>
        <DataTable
          rows={equipment}
          columns={equipmentColumns}
          loading={loading}
          rowCount={equipment.length}
          handleCellClick={(params: GridCellParams) => {
            setCurrentId(params.id.toString());
            if (params.field === 'delete') {
              setShowConfirmDelete(true);
            } else if (params.field === 'edit') {
              setMode('Edit');
              setName(params.row.name);
              setOpenAdd(true);
            }
          }}
          getData={() => {}}
        />
      </MainViewContainer>
      <GlobalModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        defaultHeader={true}
        title={`${mode} Equipment`}
        width="40vw"
      >
        <Stack spacing={1}>
          <TextField
            label="Equipment Name"
            size="small"
            value={name}
            onChange={event => setName(event.target.value)}
          />
          {mode === 'Add' ? (
            <Button variant="outlined" onClick={() => setShowConfirmAdd(true)}>
              Save
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={() => setShowConfirmUpdate(true)}
            >
              Update
            </Button>
          )}
        </Stack>
      </GlobalModal>
      <Toast
        open={showToast}
        handleClose={() => setShowToast(false)}
        text={toastData.text}
        severity={toastData.severity}
        verticalPos={toastData.verticalPos}
        horizontalPos={toastData.horizontalPos}
      />
      <Confirm
        open={showConfirmAdd}
        handleClose={() => setShowConfirmAdd(false)}
        handleConfirmed={() => {
          setShowConfirmAdd(false);
          handleAddEquipment();
        }}
        text="Are you sure you want to add this equipment?"
      />
      <Confirm
        open={showConfirmDelete}
        handleClose={() => setShowConfirmDelete(false)}
        handleConfirmed={() => {
          setShowConfirmDelete(false);
          handleDelete(currentId);
        }}
        text="Are you sure you want to delete this equipment?"
      />
      <Confirm
        open={showConfirmUpdate}
        handleClose={() => setShowConfirmUpdate(false)}
        handleConfirmed={() => {
          setShowConfirmUpdate(false);
          handleUpdate(currentId);
        }}
        text="Are you sure you want to update this equipment name?"
      />
    </>
  );
};
